import { useCallback, useEffect, useRef, useState } from "react";
import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";

/**
 * Общая логика страницы-списка: постраничная загрузка, сортировка,
 * состояния загрузки и ошибки.
 *
 * До этого 17 страниц повторяли один и тот же набор состояний дословно
 * (currentPage, totalPages, pageSize=7, orderBy, loading, error) вместе
 * с идентичным handleSort и одинаковым телом загрузки. В LessonsPage
 * набиралось 22 useState.
 *
 * Поведение сохранено ровно как было, включая:
 *   - начальный размер страницы 7;
 *   - трёхтактную сортировку: поле -> -поле -> сброс;
 *   - расчёт totalPages как Math.ceil(count / pageSize);
 *   - показ тоста с текстом ошибки и параллельную запись в error.
 */

export interface ListFetchParams {
  page: number;
  pageSize: number;
  orderBy: string;
}

export interface ListSelection<TItem> {
  items: TItem[];
  /** Общее число записей на сервере, из него считается totalPages. */
  total: number;
}

export interface UseListPageOptions<TItem, TResponse> {
  /** Запрос за страницей данных. */
  fetcher: (params: ListFetchParams) => Promise<TResponse>;
  /** Достаёт из ответа список и общее количество. */
  select: (response: TResponse) => ListSelection<TItem>;
  /** Текст для тоста и поля error. */
  errorMessage: string;
  /** Дополнительная обработка ответа: агрегаты, счётчики и подобное. */
  onLoaded?: (response: TResponse) => void;
  /** Текст, попадающий в state error. По умолчанию совпадает с errorMessage. */
  errorStateMessage?: string;
  initialPageSize?: number;
  /** Начальная сортировка. По умолчанию пустая — сервер решает сам. */
  initialOrderBy?: string;
  /** Пока false, загрузка не запускается (страница ждёт параметр из URL). */
  enabled?: boolean;
}

export interface UseListPageResult<TItem> {
  items: TItem[];
  setItems: React.Dispatch<React.SetStateAction<TItem[]>>;
  loading: boolean;
  error: string | null;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: (size: number) => void;
  totalPages: number;
  orderBy: string;
  handleSort: (field: string) => void;
  reload: () => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 7;

export function useListPage<TItem, TResponse>(
  options: UseListPageOptions<TItem, TResponse>,
): UseListPageResult<TItem> {
  const {
    fetcher,
    select,
    errorMessage,
    onLoaded,
    errorStateMessage,
    initialPageSize = DEFAULT_PAGE_SIZE,
    initialOrderBy = "",
    enabled = true,
  } = options;

  const [items, setItems] = useState<TItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize, setPageSizeState] = useState<number>(initialPageSize);
  const [orderBy, setOrderBy] = useState<string>(initialOrderBy);

  const notify = useNotify();

  // Колбэки держим в ref, чтобы страница могла объявлять их инлайном,
  // не заворачивая в useCallback и не вызывая лишних перезагрузок.
  const optionsRef = useRef({ fetcher, select, onLoaded, errorMessage, errorStateMessage });
  optionsRef.current = { fetcher, select, onLoaded, errorMessage, errorStateMessage };

  const load = useCallback(async () => {
    const current = optionsRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await current.fetcher({ page: currentPage, pageSize, orderBy });
      const { items: nextItems, total } = current.select(response);

      setItems(nextItems);
      setTotalPages(Math.ceil(total / pageSize));
      current.onLoaded?.(response);
    } catch (err) {
      logger.error(`${current.errorMessage}:`, err);
      notify.show({
        severity: "error",
        summary: "Ошибка",
        detail: current.errorMessage,
        life: 3000,
      });
      setError(current.errorStateMessage ?? current.errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, orderBy, notify]);

  useEffect(() => {
    if (!enabled) return;
    void load();
  }, [enabled, load]);

  /** Трёхтактная сортировка: по возрастанию -> по убыванию -> без сортировки. */
  const handleSort = useCallback((field: string) => {
    setOrderBy((previous) => {
      if (previous === field) return `-${field}`;
      if (previous === `-${field}`) return "";
      return field;
    });
  }, []);

  /** Смена размера страницы всегда возвращает на первую страницу. */
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setCurrentPage(1);
  }, []);

  return {
    items,
    setItems,
    loading,
    error,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    orderBy,
    handleSort,
    reload: load,
  };
}

export default useListPage;
